import { ArrowsClockwise, Cpu, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { LocalThinker, ThinkerProvider } from "~/api/aiAdminTypes";
import { SKILL_TEMPLATES, THINKER_PROVIDERS } from "~/api/aiAdminTypes";
import { useApi } from "~/api/provider";
import {
  useCreateLocalThinker,
  useDeleteLocalThinker,
  useLocalThinkerModels,
  useLocalThinkers,
  useTestLocalThinker,
  useUpdateLocalThinker,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select, TextArea } from "~/features/settings/controls";
import { FacetsEditor } from "~/features/settings/sections/thinkers/FacetsEditor";
import {
  providerLabel,
  skillTemplateHint,
  skillTemplateLabel,
} from "~/features/settings/sections/thinkers/labels";
import {
  panelId,
  type ThinkerPanel,
  ThinkerTabs,
  tabId,
} from "~/features/settings/sections/thinkers/ThinkerTabs";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog, Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

const DEFAULT_BASE_URL = "http://localhost:11434";

/** Sentinel option that swaps the model dropdown for a free-text field. */
const MANUAL_MODEL = "__manual__";

function useOwnModels(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ["thinkers", "mlx-library"],
    queryFn: () => api.research.models(),
    enabled,
    retry: false,
  });
}

const MODEL_PLACEHOLDER: Record<ThinkerProvider, string> = {
  ollama: "llama3.1:8b",
  mlx: "qwen3-small",
  openai: "gpt-4o",
  anthropic: "claude-opus-4-8",
};

interface FormState {
  displayName: string;
  provider: ThinkerProvider;
  baseUrl: string;
  model: string;
  systemPrompt: string;
  temperature: string;
  maxOutputTokens: string;
  enabled: boolean;
  skillTemplate: string;
  webMaxSourcesPerRun: string;
  webMaxCrawlDepth: string;
  webAllowedEngines: string;
}

const EMPTY_FORM: FormState = {
  displayName: "",
  provider: "ollama",
  baseUrl: DEFAULT_BASE_URL,
  model: "",
  systemPrompt: "",
  temperature: "",
  maxOutputTokens: "8192",
  enabled: true,
  skillTemplate: "author",
  webMaxSourcesPerRun: "",
  webMaxCrawlDepth: "",
  webAllowedEngines: "",
};

function formFrom(thinker: LocalThinker): FormState {
  return {
    displayName: thinker.displayName,
    provider: (THINKER_PROVIDERS as readonly string[]).includes(thinker.provider)
      ? (thinker.provider as ThinkerProvider)
      : "ollama",
    baseUrl: thinker.baseUrl || DEFAULT_BASE_URL,
    model: thinker.model,
    systemPrompt: thinker.systemPrompt ?? "",
    temperature: thinker.temperature === null ? "" : String(thinker.temperature),
    maxOutputTokens: String(thinker.maxOutputTokens),
    enabled: thinker.enabled,
    skillTemplate: thinker.skillTemplate || "author",
    webMaxSourcesPerRun:
      thinker.webMaxSourcesPerRun === null ? "" : String(thinker.webMaxSourcesPerRun),
    webMaxCrawlDepth: thinker.webMaxCrawlDepth === null ? "" : String(thinker.webMaxCrawlDepth),
    webAllowedEngines: thinker.webAllowedEngines ?? "",
  };
}

/**
 * Create or edit a local thinker.
 *
 * The model field is a dropdown built from a live probe of the Ollama host, with a free-text
 * escape hatch: a model can exist on the server and be missing from the last probe, and a
 * dropdown-only field would make it unreachable. The model already on the form is always kept
 * in the list for the same reason — otherwise editing a thinker whose model was renamed
 * silently blanks the field on save.
 */
function LocalThinkerDialog({
  thinker,
  onClose,
  initialPanel = null,
}: {
  thinker: LocalThinker | null;
  onClose: () => void;
  /** The tab to open on (`?panel=`); only a stored thinker has tabs beyond Model. */
  initialPanel?: ThinkerPanel | null;
}) {
  const { t } = useTranslation();
  const create = useCreateLocalThinker();
  const update = useUpdateLocalThinker();
  const test = useTestLocalThinker();
  const nameRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<FormState>(thinker ? formFrom(thinker) : { ...EMPTY_FORM });
  const [manualModel, setManualModel] = useState(false);
  // The dialog's tabs; remembered while it is open. A new thinker has only Model.
  const [panel, setPanel] = useState<ThinkerPanel>(
    thinker && initialPanel ? initialPanel : "model",
  );

  const probe = useLocalThinkerModels(form.baseUrl, form.provider === "ollama");
  const ownModels = useOwnModels(form.provider === "mlx");
  const models =
    form.provider === "mlx"
      ? (ownModels.data?.models ?? [])
          .filter((model) => model.status === "installed")
          .map((model) => ({ name: model.id, size: model.downloadedBytes, parameterSize: null }))
      : (probe.data?.models ?? []);

  // Focus in an effect rather than through `autoFocus`, which fights the dialog's own focus trap.
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const busy = create.isPending || update.isPending;
  const valid = form.displayName.trim().length > 0 && form.model.trim().length > 0;

  // The probed list plus whatever is already on the form, so a renamed model is still offered.
  const modelOptions =
    form.model && !models.some((model) => model.name === form.model)
      ? [...models, { name: form.model, size: null, parameterSize: null }]
      : models;

  const submit = () => {
    if (!valid) {
      return;
    }
    const draft = {
      displayName: form.displayName.trim(),
      model: form.model.trim(),
      provider: form.provider,
      baseUrl: form.baseUrl.trim() || DEFAULT_BASE_URL,
      systemPrompt: form.systemPrompt.trim() || null,
      temperature: form.temperature.trim() ? Number.parseFloat(form.temperature) : null,
      maxOutputTokens: Number.parseInt(form.maxOutputTokens, 10) || 8192,
      enabled: form.enabled,
      skillTemplate: form.skillTemplate || "author",
      webMaxSourcesPerRun: form.webMaxSourcesPerRun.trim()
        ? Number.parseInt(form.webMaxSourcesPerRun, 10)
        : null,
      webMaxCrawlDepth: form.webMaxCrawlDepth.trim()
        ? Number.parseInt(form.webMaxCrawlDepth, 10)
        : null,
      webAllowedEngines: form.webAllowedEngines.trim() || null,
    };

    const done = { onSuccess: () => onClose() };
    if (thinker) {
      update.mutate({ id: thinker.id, draft }, done);
    } else {
      create.mutate(draft, done);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={thinker ? t("Edit local thinker") : t("New local thinker")}
      closeLabel={t("Close")}
      size="sheet"
      footer={
        panel === "model" ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button variant="primary" disabled={busy || !valid} onClick={submit}>
              {thinker ? t("Save changes") : t("Create")}
            </Button>
          </>
        ) : undefined
      }
    >
      <ThinkerTabs value={panel} onChange={setPanel} saved={thinker !== null} />
      <div
        role="tabpanel"
        id={panelId("model")}
        aria-labelledby={tabId("model")}
        hidden={panel !== "model"}
        className="flex flex-col gap-3 pt-3"
      >
        <Input
          ref={nameRef}
          label={t("Name")}
          value={form.displayName}
          fullWidth
          placeholder={t("e.g. Local Llama 3")}
          onChange={(event) => setForm({ ...form, displayName: event.target.value })}
        />

        <Select
          label={t("Engine")}
          value={form.provider}
          fullWidth
          onChange={(event) =>
            setForm({ ...form, provider: event.target.value as ThinkerProvider, model: "" })
          }
        >
          {THINKER_PROVIDERS.map((provider) => (
            <option key={provider} value={provider}>
              {providerLabel(provider, t)}
            </option>
          ))}
        </Select>

        {form.provider === "ollama" ? (
          <>
            <div className="flex flex-wrap items-end gap-2">
              <Input
                label={t("Ollama server URL")}
                value={form.baseUrl}
                fullWidth
                placeholder={DEFAULT_BASE_URL}
                onChange={(event) => setForm({ ...form, baseUrl: event.target.value })}
              />
              <Button
                disabled={test.isPending}
                onClick={() =>
                  test.mutate(form.baseUrl || DEFAULT_BASE_URL, {
                    onSuccess: (result) => {
                      void probe.refetch();
                      if (result.reachable) {
                        toast.success(
                          t("Connected — {{count}} models installed", {
                            count: result.modelCount,
                          }),
                        );
                      } else {
                        toast.error(result.error ?? t("Could not reach the Ollama server."));
                      }
                    },
                  })
                }
              >
                {t("Test connection")}
              </Button>
            </div>

            {/* Unreachable is an answer, not a failure — it says which, and why. */}
            {probe.data ? (
              <Text variant="small" className={probe.data.reachable ? undefined : "text-danger"}>
                {probe.data.reachable
                  ? t("Connected — {{count}} models installed", { count: models.length })
                  : (probe.data.error ?? t("Could not reach the Ollama server."))}
              </Text>
            ) : null}
          </>
        ) : (
          <Text variant="small">
            {form.provider === "mlx"
              ? t("Uses an installed model alias from Research → Models.")
              : t("Uses the API key configured on the server for this engine.")}
          </Text>
        )}

        {(form.provider === "ollama" || form.provider === "mlx") &&
        models.length > 0 &&
        !manualModel ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-small font-medium text-text-secondary">{t("Model")}</span>
              <IconButton
                icon={ArrowsClockwise}
                aria-label={t("Refresh models")}
                size="sm"
                disabled={form.provider === "mlx" ? ownModels.isFetching : probe.isFetching}
                onClick={() =>
                  void (form.provider === "mlx" ? ownModels.refetch() : probe.refetch())
                }
              />
            </div>
            <Select
              value={form.model}
              fullWidth
              onChange={(event) => {
                if (event.target.value === MANUAL_MODEL) {
                  setManualModel(true);
                  setForm({ ...form, model: "" });
                  return;
                }
                setForm({ ...form, model: event.target.value });
              }}
            >
              <option value="">{t("Choose a model")}</option>
              {modelOptions.map((model) => (
                <option key={model.name} value={model.name}>
                  {model.parameterSize ? `${model.name} (${model.parameterSize})` : model.name}
                </option>
              ))}
              <option value={MANUAL_MODEL}>{t("Other — type a model name")}</option>
            </Select>
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Input
              label={t("Model")}
              value={form.model}
              fullWidth
              placeholder={MODEL_PLACEHOLDER[form.provider]}
              onChange={(event) => setForm({ ...form, model: event.target.value })}
            />
            {(form.provider === "ollama" || form.provider === "mlx") && models.length > 0 ? (
              <Button size="sm" variant="ghost" onClick={() => setManualModel(false)}>
                {t("Choose from the installed models")}
              </Button>
            ) : null}
          </div>
        )}

        <TextArea
          label={t("System prompt")}
          value={form.systemPrompt}
          rows={3}
          placeholder={t("Instructions put ahead of every message…")}
          onChange={(event) => setForm({ ...form, systemPrompt: event.target.value })}
        />

        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label={t("Temperature")}
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={form.temperature}
            fullWidth
            placeholder={t("Server default")}
            onChange={(event) => setForm({ ...form, temperature: event.target.value })}
          />
          <Input
            label={t("Max output tokens")}
            type="number"
            min="1"
            value={form.maxOutputTokens}
            fullWidth
            onChange={(event) => setForm({ ...form, maxOutputTokens: event.target.value })}
          />
        </div>

        <Select
          label={t("Skills")}
          value={form.skillTemplate}
          fullWidth
          hint={skillTemplateHint(form.skillTemplate, t)}
          onChange={(event) => setForm({ ...form, skillTemplate: event.target.value })}
        >
          {SKILL_TEMPLATES.map((template) => (
            <option key={template} value={template}>
              {skillTemplateLabel(template, t)}
            </option>
          ))}
        </Select>

        <div className="flex flex-col gap-1.5">
          <span className="text-small font-medium text-text-secondary">
            {t("Web gathering limits")}
          </span>
          <span className="text-small text-text-tertiary">
            {t("Blank inherits the workspace's web sources policy.")}
          </span>
          <div className="grid gap-3 md:grid-cols-3">
            <Input
              label={t("Max sources per run")}
              type="number"
              min="0"
              value={form.webMaxSourcesPerRun}
              fullWidth
              placeholder={t("Inherit")}
              onChange={(event) => setForm({ ...form, webMaxSourcesPerRun: event.target.value })}
            />
            <Input
              label={t("Max crawl depth")}
              type="number"
              min="0"
              value={form.webMaxCrawlDepth}
              fullWidth
              placeholder={t("Inherit")}
              onChange={(event) => setForm({ ...form, webMaxCrawlDepth: event.target.value })}
            />
            <Input
              label={t("Allowed engines")}
              value={form.webAllowedEngines}
              fullWidth
              placeholder={t("e.g. duckduckgo,bing")}
              onChange={(event) => setForm({ ...form, webAllowedEngines: event.target.value })}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-body text-text">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
          />
          {t("Selectable in conversations")}
        </label>
      </div>
      {/* Facets, hive and try it exist per stored row: the other tabs, all mounted so a draft
          survives a tab switch. */}
      {thinker ? (
        <FacetsEditor kind="local" id={thinker.id} name={thinker.displayName} panel={panel} />
      ) : null}
    </Dialog>
  );
}

/**
 * Models pinned to one engine and one model — an Ollama model on this machine, or a cloud
 * model this workspace wants named rather than defaulted.
 */
export function LocalPanel({
  canAdminister,
  openThinkerId = null,
  openPanel = null,
  onDialogClose,
  onOpenWorkspace,
}: {
  canAdminister: boolean;
  /** A deep link (`?local=<thinkerId>` or a row id): open that thinker's dialog on arrival. */
  openThinkerId?: string | null;
  /** …on this tab (`?panel=facets|hive|try`). */
  openPanel?: ThinkerPanel | null;
  /** The deep-linked dialog closed: the caller clears the URL parameter. */
  onDialogClose?: () => void;
  /** The deep-linked id is not a local thinker: the caller can take the person to the Workspace tab. */
  onOpenWorkspace?: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const library = useOwnModels();
  const { data: thinkers, isLoading } = useLocalThinkers();
  const bind = useMutation({
    mutationFn: (modelId: string) =>
      api.research.bind(
        modelId,
        modelId,
        modelId === "qwen3-small"
          ? "MLX Architect"
          : modelId === "qwen25-small"
            ? "MLX Reviewer"
            : modelId,
        modelId === "qwen3-small"
          ? "Architect: propose solutions, state assumptions and identify affected system parts."
          : "Reviewer: challenge claims, distinguish evidence from hypotheses and propose verification steps.",
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["thinkers"] });
      toast.success(t("Expert added to thinkers and flow selection"));
    },
  });
  const remove = useDeleteLocalThinker();

  const [editing, setEditing] = useState<LocalThinker | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<LocalThinker | null>(null);
  // The deep link opens the dialog once the list is here — once per id, so closing it sticks.
  const [missing, setMissing] = useState<string | null>(null);
  /** The open dialog came from the deep link: it may open on the linked tab. */
  const [fromLink, setFromLink] = useState(false);
  const opened = useRef<string | null>(null);
  useEffect(() => {
    if (!openThinkerId || !thinkers || opened.current === openThinkerId) return;
    opened.current = openThinkerId;
    const found = thinkers.find(
      (entry) => entry.thinkerId === openThinkerId || entry.id === openThinkerId,
    );
    if (found) {
      setMissing(null);
      setFromLink(true);
      setEditing(found);
    } else setMissing(openThinkerId);
  }, [openThinkerId, thinkers]);

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <Text variant="small">
          {t(
            "Thinkers pinned to an engine and model. MLX uses the installed host repository; Ollama uses its configured server.",
          )}
        </Text>
        {canAdminister ? (
          <Button size="sm" variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            {t("New local thinker")}
          </Button>
        ) : null}
      </div>

      <section
        className="flex flex-col gap-3 rounded border border-divider p-4"
        aria-label={t("Your downloaded models")}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-medium">{t("Your downloaded models · Direct MLX")}</h3>
          <IconButton
            icon={ArrowsClockwise}
            aria-label={t("Refresh own models")}
            onClick={() => void library.refetch()}
            disabled={library.isFetching}
          />
        </div>
        <Text variant="small">
          {t(
            "Models in your KnowIt library run through KnowIt's own MLX worker. Add an expert to make a model selectable in conversations and flows.",
          )}
        </Text>
        {library.isError ? (
          <p role="alert" className="text-small text-danger">
            {t("The MLX library could not be read from the API host.")}
          </p>
        ) : null}
        {library.data && !library.data.enabled ? (
          <p role="status" className="text-small text-warning">
            {t(
              "The direct MLX runtime is not connected on this API host. Model files on another host will not appear here until that host is connected.",
            )}
          </p>
        ) : null}
        {library.data?.models
          .filter((model) => model.status === "installed")
          .map((model) => {
            const experts = (thinkers ?? []).filter(
              (thinker) => thinker.provider === "mlx" && thinker.model === model.id,
            );
            return (
              <div
                key={model.id}
                className="flex items-center justify-between gap-3 border-t border-divider pt-3"
              >
                <div>
                  <p className="font-medium">{model.id}</p>
                  <p className="text-small text-text-secondary">
                    {model.repoId} · {(model.downloadedBytes / 1e9).toFixed(2)} GB
                  </p>
                  <p className="text-small text-text-secondary">
                    {experts.length
                      ? experts.map((expert) => expert.displayName).join(", ")
                      : t("Downloaded · no expert registered yet")}
                  </p>
                </div>
                {canAdminister && experts.length === 0 ? (
                  <Button
                    size="sm"
                    disabled={bind.isPending || !library.data.enabled}
                    onClick={() => bind.mutate(model.id)}
                  >
                    {t("Add expert")}
                  </Button>
                ) : null}
              </div>
            );
          })}
        {library.data?.enabled &&
        !library.data.models.some((model) => model.status === "installed") ? (
          <Text variant="small">
            {t("No downloaded MLX models were found in this host's library.")}
          </Text>
        ) : null}
        {bind.isError ? (
          <p role="alert" className="text-small text-danger">
            {bind.error.message}
          </p>
        ) : null}
        <Link to="/research" className="text-small text-link underline">
          {t("Open Research to download models or create the council")}
        </Link>
      </section>

      {thinkers?.length ? (
        <Rows>
          {thinkers.map((thinker) => (
            <Row key={thinker.id}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">{thinker.displayName}</p>
                <p className="truncate text-small text-text-tertiary">
                  {providerLabel(thinker.provider, t)} · {thinker.model}
                </p>
                <p className="text-small text-text-tertiary">
                  {t("Context")}: {String(thinker.capabilities?.maxContextTokens ?? "unknown")} ·{" "}
                  {t("Output")}: {thinker.maxOutputTokens}
                </p>
                {thinker.installation && (
                  <details className="text-small text-text-tertiary">
                    <summary>{t("Installed model details")}</summary>
                    <pre className="whitespace-pre-wrap break-all">
                      {JSON.stringify(thinker.installation, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
              {thinker.skillTemplate ? (
                <span className="hidden text-small text-text-tertiary md:inline">
                  {skillTemplateLabel(thinker.skillTemplate, t)}
                </span>
              ) : null}
              <Pill tone={thinker.enabled ? "success" : "neutral"}>
                {thinker.enabled ? t("Enabled") : t("Disabled")}
              </Pill>
              <span className="hidden text-small text-text-tertiary lg:inline">
                {formatRelativeTime(thinker.updatedAt, i18n.language)}
              </span>
              {canAdminister ? (
                <>
                  <IconButton
                    icon={PencilSimple}
                    aria-label={t("Edit")}
                    size="sm"
                    onClick={() => {
                      setFromLink(false);
                      setEditing(thinker);
                    }}
                  />
                  <IconButton
                    icon={Trash}
                    aria-label={t("Delete")}
                    size="sm"
                    onClick={() => setDeleting(thinker)}
                  />
                </>
              ) : null}
            </Row>
          ))}
        </Rows>
      ) : (
        <Empty
          icon={Cpu}
          message={t("No local thinkers yet. Create one to run a model on this computer.")}
        />
      )}

      <Text variant="small">
        {t("Download MLX models in Research → Models, or configure an Ollama server.")}
      </Text>

      {missing ? (
        <p role="status" className="text-small text-text-secondary">
          {t("No local thinker called {{id}} — it may be a workspace persona.", { id: missing })}{" "}
          {onOpenWorkspace ? (
            <button
              type="button"
              className="underline"
              onClick={() => {
                const id = missing;
                setMissing(null);
                onOpenWorkspace(id);
              }}
            >
              {t("Open the Workspace tab")}
            </button>
          ) : null}
        </p>
      ) : null}

      {creating ? <LocalThinkerDialog thinker={null} onClose={() => setCreating(false)} /> : null}
      {editing ? (
        <LocalThinkerDialog
          thinker={editing}
          initialPanel={fromLink ? openPanel : null}
          onClose={() => {
            setEditing(null);
            setFromLink(false);
            onDialogClose?.();
          }}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={t("Delete {{name}}?", { name: deleting.displayName })}
          description={t("This cannot be undone.")}
          confirmLabel={t("Delete")}
          cancelLabel={t("Cancel")}
          closeLabel={t("Close")}
          dangerous
          busy={remove.isPending}
          onConfirm={() =>
            remove.mutate(deleting.id, {
              onSuccess: () => {
                toast.success(t("Local thinker deleted"));
                setDeleting(null);
              },
            })
          }
        />
      ) : null}
    </div>
  );
}
